<template>
  <div class="flex schedule-answer-radio-group">
    <schedule-radio
      v-for="candidate in scheduleRadios"
      :key="candidate.schedule_candidate_date"
      :date="candidate.date"
      :canNotPossible="true"
      :isEdit="isEdit"
      v-model="candidate.time_type"
      :selectableTimeType="candidate.selectable_time_type"
      :disabled="disabled"
    />
  </div>
</template>
<script lang="ts">
import { defineComponent, watch, reactive } from 'vue'
import { AxiosResponse } from 'axios'
import ScheduleRadio from '@/components/inputs/ScheduleRadio.vue'
import { ScheduleAnserGet, ScheduleAnswerDatePost, ScheduleCandidateDate } from '@/api'
import { JrScheduleAnswerRequest } from '@/requests/scheduleAnswer'
import { ListRequest as AnswerListRequest } from '@/requests/scheduleAnswer'
import { ListRequest as CandidateListRequest } from '@/requests/schedule'

/**
 * 表示用候補日
 */
interface Schedule extends ScheduleAnswerDatePost {
  schedule_candidate_date: number
  date: string
  selectable_time_type: number
}

/**
 * 日程選択グループ(回答)
 */
export default defineComponent({
  name: 'ScheduleAnswerRadioGroup',
  emits: ['update:answer', 'update:schedule_inquiry_id'],
  components: {
    ScheduleRadio,
  },
  props: {
    messageId: {
      type: Number,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    shainCode: {
      type: String,
      required: false,
    },
    allTimeType: {
      type: Number,
      required: false,
    },
  },
  setup(props, context) {
    const scheduleRadios = reactive<Schedule[]>([])

    watch(
      () => props.messageId,
      async () => {
        scheduleRadios.splice(0)
        // 候補取得
        const candidateRequest = new CandidateListRequest(props.messageId)
        if (!(await candidateRequest.exec())) {
          // 候補取得失敗
          return
        }
        const { data: candidates } = candidateRequest.getResponse() as AxiosResponse<ScheduleCandidateDate[]>

        let schedules: Array<ScheduleAnserGet>
        if (!props.shainCode) {
          //社員コードを指定しない場合（申請者日程調整）
          // 回答取得
          const answerRequest = new AnswerListRequest(props.messageId)
          if (!(await answerRequest.exec())) {
            // 回答取得失敗
            return
          }
          const { data } = answerRequest.getResponse() as AxiosResponse<ScheduleAnserGet[]>
          schedules = data
        } else {
          //社員コードを指定する場合（JR日程調整）
          // 回答取得
          const answerRequest = new JrScheduleAnswerRequest(props.messageId, props.shainCode)
          if (!(await answerRequest.exec())) {
            // 回答取得失敗
            return
          }
          const { data } = answerRequest.getResponse() as AxiosResponse<ScheduleAnserGet[]>
          schedules = data
        }

        if (schedules.length !== 1) {
          throw new Error(`回答情報が取得できませんでした。 message_id: ${props.messageId}`)
        }
        const answers = schedules[0].schedule_answer_date
        // 回答IDをemit
        context.emit('update:schedule_inquiry_id', schedules[0].schedule_inquiry_id)

        // Radioの選択肢を作成する
        scheduleRadios.push(
          // 選択肢に成型する
          ...candidates.map((candidate) => {
            // 回答情報から同じ日付IDの情報を取得
            const answer = answers.find(
              (answer) => answer.schedule_candidate_date === candidate.schedule_candidate_date_id
            )

            let timeType = -1
            if (answer == null) {
              if (props.isEdit) {
                // 編集時は回答が無い場合候補日を選択する
                timeType = candidate.selectable_time_type
              } else {
                // 表示時は空にする
                timeType = -1
              }
            } else {
              timeType = answer.time_type
            }

            return {
              schedule_candidate_date: candidate.schedule_candidate_date_id as number,
              date: candidate.date,
              // 回答が無い場合は選択可能時間、回答が有る場合はその時間を初期値として与える
              time_type: timeType,
              selectable_time_type: candidate.selectable_time_type,
            }
          })
        )
      },
      { immediate: true }
    )

    watch(
      () => scheduleRadios,
      () => {
        context.emit(
          'update:answer',
          scheduleRadios.map((radio) => {
            return {
              schedule_candidate_date: radio.schedule_candidate_date,
              time_type: radio.time_type,
            } as ScheduleAnswerDatePost
          })
        )
      },
      { deep: true }
    )

    watch(
      // 候補日を一括で変更する。
      () => props.allTimeType,
      async () => {
        if (props.allTimeType == null) {
          return
        }
        scheduleRadios.forEach((scheduleRadio) => {
          scheduleRadio.time_type = props.allTimeType as number
        })
      },
      { immediate: true }
    )

    return {
      scheduleRadios,
    }
  },
})
</script>

<style lang="scss" scoped>
.schedule-answer-radio-group {
  max-width: 100%;
  overflow-x: auto;
}
</style>
